<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div class="modal-container">
        <h3>{{ modalTitle }}</h3>
        <form @submit.prevent="submitForm" enctype="multipart/form-data">
          
          <!-- Title -->
          <div class="form-group">
            <label>Title</label>
            <input type="text" v-model="form.title" class="form-control" />
          </div>

          <!-- Category Select -->
          <div class="form-group">
            <label>{{ $t('category') }}</label>
            <select v-model="form.expense_category_id" class="form-control" required>
              <option value="" disabled>Select Category</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.name }}
              </option>
            </select>
          </div>

          <!-- Amount & Date for Non-Salary -->
          <template v-if="selectedCategoryName !== 'Salary'">
            <div class="form-group">
              <label>{{ $t('amount') }}</label>
              <input type="number" v-model="form.amount" class="form-control" required />
            </div>

            <div class="form-group">
              <label>Date</label>
              <input
                type="date"
                v-model="form.month_year"
                class="form-control"
                placeholder="YYYY-MM-DD"
              />
            </div>
          </template>

          <!-- Salary Category -->
          <template v-else>
            <div class="form-group">
              <label>User Name</label>
              <select v-model="form.user_id" @change="fetchSalary" class="form-control">
                <option value="" disabled>Select User</option>
                <option v-for="user in users" :key="user.id" :value="user.id">
                  {{ user.full_name }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label>Salary Amount</label>
              <input type="number" v-model="form.amount" class="form-control" readonly />
            </div>

            <div class="form-group">
              <label>Month - Year</label>
              <input
                type="month"
                v-model="form.month_year"
                class="form-control"
                placeholder="YYYY-MM"
              />
            </div>
          </template>

          <!-- Description -->
          <div class="form-group">
            <label>{{ $t('description') }}</label>
            <textarea v-model="form.description" class="form-control"></textarea>
          </div>

          <!-- File Upload -->
          <div class="form-group">
            <label>Attachment (optional)</label>
            <input type="file" @change="handleFileUpload" class="form-control" />
            <small v-if="expense && expense.file">
              Current File: 
              <a :href="`/${expense.file}`" target="_blank">{{ expense.file }}</a>
            </small>
          </div>

          <div class="text-end mt-3">
            <button 
              class="btn btn-primary" 
              type="submit" 
              :disabled="isSubmitting"
            >
              <span v-if="isSubmitting">{{ $t('saving') }}...</span>
              <span v-else>{{ $t('save') }}</span>
            </button>
            <button type="button" class="btn btn-secondary" @click="$emit('input', false)" :disabled="isSubmitting">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: Boolean,
    expense: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        title: '',
        expense_category_id: '',
        amount: '',
        description: '',
        user_id: '',
        employee_name: '',
        month_year: '',
      },
      file: null, // store uploaded file
      categories: [],
      users: [],
      isSubmitting: false, // NEW FLAG
    };
  },
  computed: {
    modalTitle() {
      return this.expense ? 'Edit Expense' : 'Add Expense';
    },
    selectedCategoryName() {
      const category = this.categories.find(
        (cat) => cat.id === this.form.expense_category_id
      );
      return category ? category.name : null;
    },
  },
  watch: {
    expense: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.categories.length) {
          this.populateForm(newVal);
        }
      },
    },
  },
  mounted() {
    this.fetchCategories().then(() => {
      if (this.expense) this.populateForm(this.expense);
    });
    this.fetchUsers();
  },
  methods: {
    fetchCategories() {
      return axios.get('/expense-categories').then((res) => {
        this.categories = res.data;
      });
    },
    fetchUsers() {
      axios.get('/users').then((res) => {
        this.users = res.data.data;
      });
    },
    fetchSalary() {
      if (!this.form.user_id) return;
      axios.get(`/users/${this.form.user_id}/salary`).then((res) => {
        this.form.amount = res.data.salary || 0;
      });
    },
    handleFileUpload(event) {
      this.file = event.target.files[0];
    },
    populateForm(expense) {
      const category = this.categories.find(
        (cat) => cat.id === expense.expense_category_id
      );
      const isSalary = category?.name === 'Salary';

      let monthYearValue = '';
      if (expense.month_year) {
        if (isSalary) {
          monthYearValue = expense.month_year.slice(0, 7);
        } else {
          const d = new Date(expense.month_year);
          const year = d.getFullYear();
          const month = ('0' + (d.getMonth() + 1)).slice(-2);
          const day = ('0' + d.getDate()).slice(-2);
          monthYearValue = `${year}-${month}-${day}`;
        }
      }

      this.form = {
        title: expense.title || '',
        expense_category_id: expense.expense_category_id || '',
        amount: expense.amount || '',
        description: expense.description || '',
        user_id: expense.user_id || '',
        employee_name: expense.employee_name || '',
        month_year: monthYearValue,
      };
    },
    resetForm() {
      this.form = {
        title: '',
        expense_category_id: '',
        amount: '',
        description: '',
        user_id: '',
        employee_name: '',
        month_year: '',
      };
      this.file = null;
    },
    submitForm() {
      this.isSubmitting = true; // disable button
      const url = this.expense ? `/expenses/${this.expense.id}` : '/expenses';
      const method = 'post'; // always post, use _method for update

      const formData = new FormData();
      Object.keys(this.form).forEach((key) => {
        formData.append(key, this.form[key]);
      });

      if (this.file) {
        formData.append('file', this.file);
      }

      if (this.expense) {
        formData.append('_method', 'PUT'); // Laravel expects this for update
      }

      axios({
        method,
        url,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(() => {
          this.$emit('input', false);
          this.$emit('saved');
          this.resetForm();
        })
        .catch((error) => {
          alert('Failed to save expense. Please check your input.');
          console.error(error);
        })
        .finally(() => {
          this.isSubmitting = false; // re-enable button
        });
    },
  },
};
</script>

<style scoped>
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-wrapper {
  width: 450px;
  background: #fff;
  padding: 20px;
  border-radius: 6px;
}
</style>
